import { shallow } from "zustand/vanilla/shallow";
import { resolveConnectorToolCall } from "./connectorToolPresentation";
import {
  isToolDiscoveryEntry,
  isWorkspaceToolCall,
  namedToolHeading,
  workflowCallHeading,
  toolDiscoveryName,
  resolveNamedToolPresentation,
  namedToolGroupLabel,
  type NamedToolAction,
} from "./toolPresentation";
import * as Equal from "effect/Equal";
import {
  formatDuration,
  inferCheckpointTurnCountByTurnId,
  isStreamingMessageTextUpdate,
  workEntryDisplayIndicatesToolFailure,
  workEntryIndicatesToolNeutralStatus,
  workEntryIndicatesToolSuccess,
  workLogEntryIsToolLike,
  type TimelineEntry,
  type TurnPlanEntry,
  type WorkLogEntry,
} from "../../session-logic";
import { type ChatMessage, type TurnDiffSummary } from "../../types";
import { type MessageId, type OrchestrationLatestTurn, type TurnId } from "@amoreg/contracts";

export const TIMELINE_MINIMAP_ITEM_SPACING = 8;
export const TIMELINE_MINIMAP_MIN_ITEMS = 2;
export const TIMELINE_MINIMAP_MAX_HEIGHT_CSS = "calc(100vh - 18rem)";
export const TIMELINE_CONTENT_MAX_WIDTH = 768;
export const TIMELINE_MINIMAP_PERSISTENT_GUTTER = 48;

export function workEntryIsVisibleInGroup(
  entry: WorkLogEntry,
  expandedToolGroupEntry = false,
): boolean {
  return (
    (expandedToolGroupEntry &&
      (entry.toolLifecycleStatus === "inProgress" ||
        entry.sourceActivityKind === "task.progress")) ||
    !workEntryIndicatesToolNeutralStatus(entry)
  );
}

export interface TimelineEndState {
  readonly isAtEnd?: boolean;
  readonly contentLength?: number;
  readonly scroll?: number;
  readonly scrollLength?: number;
}

/**
 * Follow re-arm band above the hard bottom. Strict on purpose: LegendList's
 * isNearEnd fires within half a viewport, which re-armed live-follow while the
 * user was reading history and yanked them back down on the next stream chunk.
 * A small pixel band (instead of the 1px isAtEnd epsilon alone) keeps re-arming
 * reliable while streaming content is still growing under the viewport.
 */
export const TIMELINE_FOLLOW_REARM_THRESHOLD_PX = 40;

export function resolveTimelineIsAtEnd(
  state: TimelineEndState | undefined,
  endInset = 0,
): boolean | undefined {
  if (!state) {
    return undefined;
  }
  if (state.isAtEnd) {
    return true;
  }
  const { contentLength, scroll, scrollLength } = state;
  if (contentLength === undefined || scroll === undefined || scrollLength === undefined) {
    return state.isAtEnd;
  }
  // contentLength includes the end inset (composer overlay), so subtract it to
  // measure the distance to the real content bottom.
  return contentLength - scroll - scrollLength - endInset <= TIMELINE_FOLLOW_REARM_THRESHOLD_PX;
}

export function shouldPreserveAssistantLineBreaks(text: string): boolean {
  return /^★ Insight(?:\s|─)/mu.test(text);
}

export function resolveTimelineMinimapHeightStyle(itemCount: number): string {
  const naturalHeight = Math.max(1, (itemCount - 1) * TIMELINE_MINIMAP_ITEM_SPACING);
  return `min(${naturalHeight}px, ${TIMELINE_MINIMAP_MAX_HEIGHT_CSS})`;
}

export function resolveTimelineMinimapTopPercent(index: number, itemCount: number): number {
  if (itemCount <= 1) {
    return 0;
  }
  return (Math.max(0, Math.min(index, itemCount - 1)) / (itemCount - 1)) * 100;
}

export function resolveTimelineMinimapIndexFromPointer(input: {
  readonly itemCount: number;
  readonly railTop: number;
  readonly railHeight: number;
  readonly pointerY: number;
}): number | null {
  if (input.itemCount <= 0 || input.railHeight <= 0) {
    return null;
  }
  if (input.itemCount === 1) {
    return 0;
  }

  const progress = Math.max(0, Math.min(1, (input.pointerY - input.railTop) / input.railHeight));
  return Math.max(0, Math.min(input.itemCount - 1, Math.round(progress * (input.itemCount - 1))));
}

export function resolveTimelineMinimapCurrentIndex(input: {
  readonly scrollTop: number;
  readonly scrollBottom: number;
  readonly itemBounds: ReadonlyArray<{
    readonly top: number | null;
    readonly height: number | null;
  }>;
}): number | null {
  let precedingIndex: number | null = null;

  for (const [index, item] of input.itemBounds.entries()) {
    if (item.top === null) {
      continue;
    }
    const inView =
      item.top < input.scrollBottom && item.top + Math.max(1, item.height ?? 1) > input.scrollTop;
    if (inView) {
      // The first visible marker is the turn at the reader's current position.
      return index;
    }
    if (item.top <= input.scrollTop) {
      precedingIndex = index;
    }
  }

  return precedingIndex;
}

export function resolveTimelineMinimapHasPersistentGutter(viewportWidth: number): boolean {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) {
    return false;
  }

  const contentWidth = Math.min(viewportWidth, TIMELINE_CONTENT_MAX_WIDTH);
  const sideGutter = Math.max(0, (viewportWidth - contentWidth) / 2);
  return sideGutter >= TIMELINE_MINIMAP_PERSISTENT_GUTTER;
}

export const TIMELINE_MINIMAP_HIT_STRIP_LEFT = 12;
export const TIMELINE_MINIMAP_HIT_STRIP_MAX_WIDTH = 40;
export const TIMELINE_MINIMAP_EXPANDED_HIT_STRIP_WIDTH = "22rem";

/**
 * The minimap overlays the viewport's left edge while the content column is
 * centered, so the side gutter between them shrinks under browser zoom or a
 * narrow pane. A fixed-width hover strip would then sit on top of the message
 * text and swallow its pointer events. Cap the strip's width so it never
 * extends past the gutter into the content column; 0 disables the strip.
 */
export function resolveTimelineMinimapHitStripWidth(viewportWidth: number): number {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) {
    return 0;
  }

  const contentWidth = Math.min(viewportWidth, TIMELINE_CONTENT_MAX_WIDTH);
  const sideGutter = Math.max(0, (viewportWidth - contentWidth) / 2);
  return Math.max(
    0,
    Math.min(
      TIMELINE_MINIMAP_HIT_STRIP_MAX_WIDTH,
      Math.floor(sideGutter) - TIMELINE_MINIMAP_HIT_STRIP_LEFT,
    ),
  );
}

/**
 * Once the preview is open, keep the full preview and the space leading to it
 * interactive. The collapsed strip remains gutter-capped so it cannot block
 * selecting message text.
 */
export function resolveTimelineMinimapInteractiveWidth(
  collapsedWidth: number,
  expanded: boolean,
): number | string {
  return expanded ? TIMELINE_MINIMAP_EXPANDED_HIT_STRIP_WIDTH : collapsedWidth;
}

function computeElapsedMs(startIso: string, endIso: string): number | null {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}

function maxIsoTimestamp(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  const aMs = Date.parse(a);
  const bMs = Date.parse(b);
  if (!Number.isFinite(aMs)) return b;
  if (!Number.isFinite(bMs)) return a;
  return bMs > aMs ? b : a;
}

export interface TimelineDurationMessage {
  id: string;
  role: ChatMessage["role"];
  createdAt: string;
  updatedAt: string;
  streaming: boolean;
}

export type TimelineLatestTurn = Pick<
  OrchestrationLatestTurn,
  "turnId" | "state" | "startedAt" | "completedAt"
>;

/**
 * The one row that carries the live activity of a running turn — the changing
 * tool line or the "Thinking" placeholder. A stable id keeps the row mounted
 * (and its shimmer running) while thoughts and tools alternate underneath it.
 */
export const LIVE_ACTIVITY_ROW_ID = "live-activity-row";

type ActivityEntry = Extract<TimelineEntry, { kind: "message" | "work" }>;

/**
 * Thoughts and ordinary tool calls of one turn render as a single activity row.
 * User input, subagent batches, compaction markers, warnings and errors keep
 * their own rows, as they do inside plain tool groups.
 */
function isActivityEntry(entry: TimelineEntry): entry is ActivityEntry {
  return entry.kind === "message"
    ? entry.message.role === "reasoning"
    : entry.kind === "work" &&
        entry.entry.agentSpawn === undefined &&
        entry.entry.questionAnswer === undefined &&
        entry.entry.sourceActivityKind !== "context-compaction" &&
        // A warning (a token limit pausing the chat) is news for the person,
        // not a step of the work: folded into the thoughts it was a grey,
        // truncated line they read past.
        entry.entry.sourceActivityKind !== "runtime.warning" &&
        entry.entry.tone !== "error";
}

export type MessagesTimelineRow =
  | {
      kind: "activity-group";
      id: string;
      createdAt: string;
      turnId: TurnId;
      groupId: string;
      entries: ActivityEntry[];
      expanded: boolean;
      active: boolean;
    }
  | {
      kind: "work";
      id: string;
      createdAt: string;
      groupedEntries: WorkLogEntry[];
      /** The details of an expanded tool group: one bounded, scrollable row. */
      isExpandedToolGroup: boolean;
      /** A lone edit reads as its summary ("Changed 1 file") rather than a path. */
      displayLabel?: string | undefined;
    }
  | {
      kind: "work-live";
      id: string;
      createdAt: string;
      entry: WorkLogEntry;
      groupedEntries: WorkLogEntry[];
      groupId: string;
      expanded: boolean;
      /** Whether the turn is still producing activity behind this row. */
      active: boolean;
    }
  | {
      kind: "work-toggle";
      id: string;
      createdAt: string;
      turnId: TurnId | null;
      groupId: string;
      hiddenCount: number;
      expanded: boolean;
      summary: string;
      summaryKind: ToolGroupSummaryKind;
      toolWebsiteUrl?: string;
      /** The one connected app every call in the group went to: its mark. */
      connectorId?: string;
      hasFailure: boolean;
    }
  | {
      kind: "turn-fold";
      id: string;
      createdAt: string;
      turnId: TurnId;
      label: string;
      expanded: boolean;
    }
  | {
      kind: "context-compaction";
      id: string;
      createdAt: string;
      label: string;
    }
  | {
      kind: "message";
      id: string;
      createdAt: string;
      message: ChatMessage;
      durationStart: string;
      showAssistantMeta: boolean;
      showAssistantCopyButton: boolean;
      assistantCopyStreaming: boolean;
      assistantTurnDiffSummary?: TurnDiffSummary | undefined;
      revertTurnCount?: number | undefined;
    }
  | {
      /**
       * The metadata footer of a terminal assistant message, detached from the
       * message when a trailing tool group belongs to the same response.
       */
      kind: "assistant-meta";
      id: string;
      createdAt: string;
      message: ChatMessage;
      showAssistantCopyButton: boolean;
      assistantCopyStreaming: boolean;
    }
  | {
      kind: "turn-plan";
      id: string;
      createdAt: string;
      turnPlan: TurnPlanEntry;
    }
  | {
      kind: "working";
      id: string;
      createdAt: string | null;
    }
  | {
      /** The "Thinking" placeholder while the running turn has no live tool. */
      kind: "thinking";
      id: string;
      createdAt: string | null;
    };

export interface StableMessagesTimelineRowsState {
  byId: Map<string, MessagesTimelineRow>;
  result: MessagesTimelineRow[];
}

export function computeMessageDurationStart(
  messages: ReadonlyArray<TimelineDurationMessage>,
): Map<string, string> {
  const result = new Map<string, string>();
  let lastBoundary: string | null = null;

  for (const message of messages) {
    if (message.role === "user") {
      lastBoundary = message.createdAt;
    }
    result.set(message.id, lastBoundary ?? message.createdAt);
    if (message.role === "assistant" && !message.streaming) {
      lastBoundary = message.updatedAt;
    }
  }

  return result;
}

export interface WorkGroupScrollAnchor {
  readonly entryId: string;
  readonly offset: number;
}

/** Restore a visible tool, including a position partway through its expanded output. */
export function resolveWorkGroupScrollIndex(
  entries: ReadonlyArray<{ readonly id: string }>,
  anchor: WorkGroupScrollAnchor | undefined,
): { index: number; viewOffset: number } | undefined {
  if (!anchor) return undefined;
  const index = entries.findIndex((entry) => entry.id === anchor.entryId);
  return index < 0 ? undefined : { index, viewOffset: -anchor.offset };
}

/** Only newly appended calls may follow the end, never status or output updates. */
export function shouldFollowWorkGroupAppend(
  previous: ReadonlyArray<{ readonly id: string }>,
  entries: ReadonlyArray<{ readonly id: string }>,
  distanceFromEnd: number,
): boolean {
  return (
    previous.length > 0 &&
    entries.length > previous.length &&
    distanceFromEnd <= 1 &&
    previous.every((entry, index) => entry.id === entries[index]?.id)
  );
}

export function normalizeCompactToolLabel(value: string): string {
  return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}

type ToolGroupAction =
  | NamedToolAction
  | "read"
  | "edit"
  | "command"
  | "code-search"
  | "search"
  | "other";
export type ToolGroupSummaryKind =
  | ToolGroupAction
  | "dynamic-tool"
  | "agent-tool"
  | "tone-tool"
  | "workspace-tool"
  | "mixed";

export function workLogEntryIsLocalCodeSearch(entry: WorkLogEntry): boolean {
  return (
    entry.itemType === "web_search" &&
    /\bgrep\b/i.test(normalizeCompactToolLabel(entry.toolTitle ?? entry.label))
  );
}

export function toolGroupAction(entry: WorkLogEntry): ToolGroupAction {
  const namedTool = resolveNamedToolPresentation(entry);
  if (namedTool) return namedTool.action;
  if (
    entry.requestKind === "file-read" ||
    entry.itemType === "image_view" ||
    (entry.itemType === "dynamic_tool_call" &&
      normalizeCompactToolLabel(entry.toolTitle ?? entry.label).toLowerCase() === "read file")
  ) {
    return "read";
  }
  if (
    entry.requestKind === "file-change" ||
    entry.itemType === "file_change" ||
    (entry.changedFiles?.length ?? 0) > 0
  ) {
    return "edit";
  }
  if (entry.requestKind === "command" || entry.itemType === "command_execution" || entry.command) {
    return "command";
  }
  if (workLogEntryIsLocalCodeSearch(entry)) return "code-search";
  if (entry.itemType === "web_search") return "search";
  return "other";
}

function toolGroupActionCount(
  action: ToolGroupAction,
  entries: ReadonlyArray<WorkLogEntry>,
): number {
  if (action !== "edit") return entries.length;

  const changedFiles = new Set<string>();
  let editsWithoutFileDetails = 0;
  for (const entry of entries) {
    if (!entry.changedFiles || entry.changedFiles.length === 0) {
      editsWithoutFileDetails += 1;
      continue;
    }
    for (const file of entry.changedFiles) changedFiles.add(file);
  }
  return changedFiles.size + editsWithoutFileDetails;
}

function toolGroupActionLabel(action: ToolGroupAction, count: number): string {
  switch (action) {
    case "read":
      return `Read ${count} ${count === 1 ? "file" : "files"}`;
    case "edit":
      return `Changed ${count} ${count === 1 ? "file" : "files"}`;
    case "command":
      return `Ran ${count} ${count === 1 ? "command" : "commands"}`;
    case "search":
      return `Searched the web ${count} ${count === 1 ? "time" : "times"}`;
    case "code-search":
      return `Searched code ${count} ${count === 1 ? "time" : "times"}`;
    case "other":
      return `Used ${count} ${count === 1 ? "tool" : "tools"}`;
    default:
      return namedToolGroupLabel(action, count);
  }
}

/** Immediate, provider-neutral fallback while generated tool summaries are disabled or unavailable. */
export function summarizeToolGroup(entries: ReadonlyArray<WorkLogEntry>): string {
  const summaryEntries = omitSupersededLifecycleMarkers(entries, (entry) => entry);
  const groupedEntries = new Map<ToolGroupAction, WorkLogEntry[]>();
  for (const entry of summaryEntries) {
    const action = toolGroupAction(entry);
    const group = groupedEntries.get(action);
    if (group) group.push(entry);
    else groupedEntries.set(action, [entry]);
  }
  const labels = [...groupedEntries].map(
    ([action, actionEntries]) =>
      (action === "integration" ? connectorGroupLabel(actionEntries) : null) ??
      (action === "workflows" && actionEntries.length === 1
        ? workflowCallHeading(actionEntries[0]!)
        : null) ??
      toolGroupActionLabel(action, toolGroupActionCount(action, actionEntries)),
  );
  const sentenceLabels = labels.map((label, index) =>
    index === 0 ? label : label.charAt(0).toLowerCase() + label.slice(1),
  );
  if (sentenceLabels.length < 2) return sentenceLabels[0] ?? "";
  if (sentenceLabels.length === 2) return sentenceLabels.join(" and ");
  return `${sentenceLabels.slice(0, -1).join(", ")}, and ${sentenceLabels.at(-1)}`;
}

/**
 * Drops rows a later row already stands for: an anonymous lifecycle marker
 * its call's completion supersedes, and a tool lookup once a later call in
 * its turn used what it found — `connection_search` by a connection's tool,
 * the account check by an app action, the action search by an action in the
 * same app. A lookup still running, failed, declined or stopped stays: it is
 * the news.
 */
/**
 * The one connected app a run of calls used, when there was only one: its
 * mark stands for the group, and its name for "connected apps".
 */
export function soleConnectorId(entries: ReadonlyArray<WorkLogEntry>): string | null {
  let connectorId: string | null = null;
  for (const entry of entries) {
    if (isToolDiscoveryEntry(entry) && resolveConnectorToolCall(entry) === null) continue;
    const call = resolveConnectorToolCall(entry);
    if (call === null) return null;
    if (connectorId !== null && call.connectorId !== connectorId) return null;
    connectorId = call.connectorId;
  }
  return connectorId;
}

/** "Searched emails" for one Gmail call, "Used Gmail 3 times" for several. */
function connectorGroupLabel(entries: ReadonlyArray<WorkLogEntry>): string | null {
  if (soleConnectorId(entries) === null) return null;
  const calls = entries.filter((entry) => resolveConnectorToolCall(entry) !== null);
  if (calls.length === 1) return namedToolHeading(calls[0]!);
  const name = resolveConnectorToolCall(calls[0]!)!.connectorName;
  return `Used ${name} ${calls.length} times`;
}

export function omitSupersededLifecycleMarkers<T>(
  entries: readonly T[],
  workEntryFor: (entry: T) => WorkLogEntry,
): T[] {
  const laterTerminalIdentities = new Set<string>();
  // Per turn, what later rows used: a connection's tools (a namespaced
  // `workspace__…` call uses what `connection_search` loaded) and the
  // connected apps a later action ran in.
  const turnsWithLaterConnectionCall = new Set<string>();
  const laterConnectorActions = new Map<string, Set<string>>();
  const reversedEntries: T[] = [];

  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]!;
    const workEntry = workEntryFor(entry);
    // Rows with no turn share nothing: each is its own scope.
    const turnKey = workEntry.turnId ?? `no-turn:${index}`;
    const lookup = toolDiscoveryName(workEntry);
    if (lookup !== null) {
      const settledQuietly =
        workEntry.toolLifecycleStatus !== "inProgress" &&
        workEntry.toolLifecycleStatus !== "declined" &&
        workEntry.toolLifecycleStatus !== "stopped" &&
        !workEntryDisplayIndicatesToolFailure(workEntry);
      const actions = laterConnectorActions.get(turnKey);
      const lookedUpConnector = resolveConnectorToolCall(workEntry)?.connectorId;
      const used =
        lookup === "connection_search"
          ? turnsWithLaterConnectionCall.has(turnKey)
          : lookup === "plugins_list_accounts" || lookedUpConnector === undefined
            ? (actions?.size ?? 0) > 0
            : (actions?.has(lookedUpConnector) ?? false);
      if (settledQuietly && used) continue;
    }
    // eve names a connection's tools `<connection>__<tool>`; the agent's one
    // connection is the workspace.
    if (workEntry.toolName?.startsWith("workspace__")) turnsWithLaterConnectionCall.add(turnKey);
    const action = resolveConnectorToolCall(workEntry);
    if (action?.toolSlug) {
      const actions = laterConnectorActions.get(turnKey) ?? new Set<string>();
      actions.add(action.connectorId);
      laterConnectorActions.set(turnKey, actions);
    }
    const normalizedLabel = normalizeCompactToolLabel(workEntry.toolTitle ?? workEntry.label);
    const identity = [
      workEntry.turnId ?? "no-turn",
      workEntry.itemType ?? "",
      normalizedLabel,
    ].join("\u001f");
    const isStatuslessIdlessMarker =
      workEntry.toolCallId === undefined &&
      workEntry.toolLifecycleStatus === undefined &&
      (workEntry.sourceActivityKind === "tool.started" ||
        workEntry.sourceActivityKind === "tool.updated");
    if (isStatuslessIdlessMarker && laterTerminalIdentities.has(identity)) continue;

    reversedEntries.push(entry);
    if (
      workEntry.sourceActivityKind === "tool.completed" ||
      (workEntry.toolLifecycleStatus !== undefined &&
        workEntry.toolLifecycleStatus !== "inProgress")
    ) {
      laterTerminalIdentities.add(identity);
    }
  }

  return reversedEntries.toReversed();
}

export function toolGroupSummaryKind(entries: ReadonlyArray<WorkLogEntry>): ToolGroupSummaryKind {
  const actions = new Set(entries.map(toolGroupAction));
  if (actions.size !== 1) return "mixed";

  const action = actions.values().next().value!;
  if (action !== "other") return action;

  const fallbackKinds = new Set(
    entries.map((entry): ToolGroupSummaryKind => {
      if (isWorkspaceToolCall(entry)) return "workspace-tool";
      if (entry.itemType === "mcp_tool_call") return "other";
      if (entry.itemType === "dynamic_tool_call") return "dynamic-tool";
      if (entry.itemType === "collab_agent_tool_call" || entry.taskId) return "agent-tool";
      if (entry.tone === "thinking") return "agent-tool";
      if (entry.tone === "tool") return "tone-tool";
      return "other";
    }),
  );
  return fallbackKinds.size === 1 ? fallbackKinds.values().next().value! : "mixed";
}

function workGroupIdentity(timelineEntryId: string, entry: WorkLogEntry): string {
  return entry.toolCallId
    ? `tool:${entry.turnId ?? "no-turn"}:${entry.toolCallId}`
    : timelineEntryId;
}

function workGroupId(timelineEntryId: string, entry: WorkLogEntry): string {
  return `work-group:${workGroupIdentity(timelineEntryId, entry)}`;
}

function expandedWorkGroupRow(
  groupId: string,
  createdAt: string,
  groupedEntries: WorkLogEntry[],
): Extract<MessagesTimelineRow, { kind: "work" }> {
  return {
    kind: "work",
    id: `${groupId}:details`,
    createdAt,
    groupedEntries,
    isExpandedToolGroup: true,
  };
}

export function resolveAssistantMessageCopyState({
  text,
  showCopyButton,
  streaming,
}: {
  text: string | null;
  showCopyButton: boolean;
  streaming: boolean;
}) {
  const hasText = text !== null && text.trim().length > 0;
  return {
    text: hasText ? text : null,
    visible: showCopyButton && hasText && !streaming,
  };
}

function deriveTerminalAssistantMessageIds(timelineEntries: ReadonlyArray<TimelineEntry>) {
  const lastAssistantMessageIdByResponseKey = new Map<string, string>();
  let nullTurnResponseIndex = 0;

  for (const timelineEntry of timelineEntries) {
    if (timelineEntry.kind !== "message") {
      continue;
    }
    const { message } = timelineEntry;
    if (message.role === "user") {
      nullTurnResponseIndex += 1;
      continue;
    }
    if (message.role !== "assistant") {
      continue;
    }

    const responseKey = message.turnId
      ? `turn:${message.turnId}`
      : `unkeyed:${nullTurnResponseIndex}`;
    lastAssistantMessageIdByResponseKey.set(responseKey, message.id);
  }

  return new Set(lastAssistantMessageIdByResponseKey.values());
}

interface TurnFold {
  turnId: TurnId;
  anchorEntryId: string;
  createdAt: string;
  hiddenEntryIds: ReadonlySet<string>;
  label: string;
  /**
   * A response that delegated across several turns (one spawn per wake)
   * shows ONE "Ran N subagents" row while folded: the first direct-spawn row
   * carries every agent of the response and the rest hide with the fold.
   */
  mergedSpawn: {
    entryId: string;
    entry: WorkLogEntry;
    hiddenEntryIds: ReadonlySet<string>;
  } | null;
}

function spawnEntryIsLive(entry: WorkLogEntry, liveAgentTaskIds: ReadonlySet<string>): boolean {
  const spawn = entry.agentSpawn;
  return (
    spawn !== undefined &&
    ((spawn.workflowId !== null && liveAgentTaskIds.has(spawn.workflowId)) ||
      spawn.agentTaskIds.some((taskId) => liveAgentTaskIds.has(taskId)))
  );
}

/**
 * The session's running turn is authoritative when latestTurn briefly lags or
 * regresses behind it. Otherwise, the latest turn counts as unsettled while it
 * is still running (or has not recorded a completion). This is deliberately
 * keyed on turn lifecycle rather than transient working state: right after the
 * user sends a message, the previous turn is still the "active" one until the
 * server creates the new turn, and folding must not flicker through that window.
 */
export function deriveUnsettledTurnId(
  latestTurn: TimelineLatestTurn | null,
  runningTurnId: TurnId | null,
): TurnId | null {
  if (runningTurnId !== null) {
    return runningTurnId;
  }
  if (!latestTurn) {
    return null;
  }
  const isSettled = latestTurn.completedAt !== null && latestTurn.state !== "running";
  return isSettled ? null : latestTurn.turnId;
}

function lastUserMessageIndex(timelineEntries: ReadonlyArray<TimelineEntry>): number {
  return timelineEntries.findLastIndex(
    (entry) => entry.kind === "message" && entry.message.role === "user",
  );
}

function timelineEntryTurnId(entry: TimelineEntry): TurnId | null {
  if (entry.kind === "message") {
    return entry.message.role === "assistant" || entry.message.role === "reasoning"
      ? (entry.message.turnId ?? null)
      : null;
  }
  if (entry.kind === "turn-plan") {
    return entry.turnPlan.turnId;
  }
  return entry.kind === "work" ? (entry.entry.turnId ?? null) : null;
}

/**
 * Every turn that answers the latest user message is one visual response
 * while the thread works: a steer or a restart continues the same reply, so
 * none of those turns fold and their assistant text stays provisional.
 */
function deriveActiveVisualResponseTurnIds(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  unsettledTurnId: TurnId | null;
  isWorking: boolean;
}): ReadonlySet<TurnId> {
  const turnIds = new Set<TurnId>();
  if (input.unsettledTurnId === null) {
    return turnIds;
  }

  turnIds.add(input.unsettledTurnId);
  if (!input.isWorking) {
    return turnIds;
  }

  const latestUserMessageIndex = lastUserMessageIndex(input.timelineEntries);
  for (let index = latestUserMessageIndex + 1; index < input.timelineEntries.length; index += 1) {
    const turnId = timelineEntryTurnId(input.timelineEntries[index]!);
    if (turnId !== null) {
      turnIds.add(turnId);
    }
  }
  return turnIds;
}

export function workEntryIsActiveTurnActivity(entry: WorkLogEntry): boolean {
  return (
    entry.toolLifecycleStatus === "inProgress" ||
    (entry.toolLifecycleStatus === undefined &&
      (entry.sourceActivityKind === "task.progress" || workLogEntryIsToolLike(entry)))
  );
}

/**
 * Settled turns fold activity before their terminal assistant message behind
 * a "Worked for ..." row. A single ordinary activity after that message joins
 * the fold, while larger groups and failures stay visible as a trailing summary.
 */
function deriveTurnFolds(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  terminalAssistantMessageIds: ReadonlySet<string>;
  latestTurn: TimelineLatestTurn | null;
  unfoldedTurnIds: ReadonlySet<TurnId>;
  liveAgentTaskIds: ReadonlySet<string>;
}): ReadonlyMap<string, TurnFold> {
  interface TurnGroup {
    entries: Array<TimelineEntry>;
    terminalEntry: Extract<TimelineEntry, { kind: "message" }> | null;
    hasStreamingMessage: boolean;
    /**
     * The user message that kicked the turn off. Entry timestamps alone
     * undercount the duration (the first entry appears only once the
     * provider starts producing output), and a turn cut short by a steer may
     * hold a single instantaneous commentary message.
     */
    startBoundary: string | null;
    /** Every turn this response spans (see `responseKeyByTurnId`). */
    turnIds: Set<TurnId>;
  }
  const groupsByTurnId = new Map<TurnId, TurnGroup>();
  // A turn no user message introduced — the wake that delivers a delegated
  // worker's result, a promptless restart — continues the response of the
  // turn before it, so the whole reply folds as one "Worked for …" instead of
  // one fold per turn. Keyed by the response's first turn. (AMO-37)
  const responseKeyByTurnId = new Map<TurnId, TurnId>();
  let lastResponseKey: TurnId | null = null;
  let userSinceLastTurn = true;

  let pendingUserBoundary: string | null = null;
  for (const entry of input.timelineEntries) {
    if (entry.kind === "message" && entry.message.role === "user") {
      pendingUserBoundary = entry.message.createdAt;
      userSinceLastTurn = true;
      continue;
    }
    // Thinking is work, so it folds with the rest of it. A provider that
    // interleaves a block with every tool call would otherwise leave dozens of
    // "Thought" rows standing beside the "Worked for ..." summary.
    // Nothing folds while the turn is live, which is when traces are watched.
    const turnId =
      entry.kind === "message" &&
      (entry.message.role === "assistant" || entry.message.role === "reasoning")
        ? (entry.message.turnId ?? null)
        : entry.kind === "work"
          ? (entry.entry.turnId ?? null)
          : null;
    if (!turnId) {
      continue;
    }
    let responseKey = responseKeyByTurnId.get(turnId);
    if (responseKey === undefined) {
      responseKey = userSinceLastTurn || lastResponseKey === null ? turnId : lastResponseKey;
      responseKeyByTurnId.set(turnId, responseKey);
      lastResponseKey = responseKey;
      userSinceLastTurn = false;
    }
    let group = groupsByTurnId.get(responseKey);
    if (!group) {
      group = {
        entries: [],
        terminalEntry: null,
        hasStreamingMessage: false,
        // Each user boundary starts at most one response.
        startBoundary: pendingUserBoundary,
        turnIds: new Set(),
      };
      pendingUserBoundary = null;
      groupsByTurnId.set(responseKey, group);
    }
    group.turnIds.add(turnId);
    group.entries.push(entry);
    if (entry.kind === "message") {
      if (input.terminalAssistantMessageIds.has(entry.message.id)) {
        group.terminalEntry = entry;
      }
      // A live turn is already excluded above, so only an answer still being
      // written may hold a fold open. A thinking block stranded by a crashed
      // provider keeps its streaming flag forever and must not.
      if (entry.message.streaming && entry.message.role !== "reasoning") {
        group.hasStreamingMessage = true;
      }
    }
  }

  const foldsByAnchorEntryId = new Map<string, TurnFold>();
  for (const [turnId, group] of groupsByTurnId) {
    if (Array.from(group.turnIds).some((id) => input.unfoldedTurnIds.has(id))) {
      continue;
    }
    if (group.hasStreamingMessage) {
      continue;
    }
    // A delegation runs on while its parent turn is parked: the turn settles,
    // but the response is not done until its subagents are, and the wake
    // that delivers their result continues it.
    if (
      group.entries.some(
        (entry) => entry.kind === "work" && spawnEntryIsLive(entry.entry, input.liveAgentTaskIds),
      )
    ) {
      continue;
    }
    const hiddenEntryIds = new Set<string>();
    const terminalEntryIndex = group.terminalEntry
      ? group.entries.findIndex((entry) => entry.id === group.terminalEntry?.id)
      : group.entries.length;
    // Thinking blocks do not count toward "one trailing activity": a block can
    // follow the answer, and it must not stop that lone tool call from folding
    // the way it did before traces existed. Loop-invariant, so it is counted
    // once: a long turn re-derives these rows on every work-log change.
    const trailingEntryCount = group.entries.filter(
      (candidate, candidateIndex) =>
        candidateIndex > terminalEntryIndex &&
        !(candidate.kind === "message" && candidate.message.role === "reasoning"),
    ).length;
    for (const [index, entry] of group.entries.entries()) {
      if (entry.id === group.terminalEntry?.id) {
        continue;
      }
      const isCompaction =
        entry.kind === "work" && entry.entry.sourceActivityKind === "context-compaction";
      const isSingleTrailingActivity =
        trailingEntryCount === 1 &&
        entry.kind === "work" &&
        !workEntryDisplayIndicatesToolFailure(entry.entry);
      // A thinking block after the answer folds with its turn rather than
      // trailing under it.
      const isReasoning = entry.kind === "message" && entry.message.role === "reasoning";
      if (
        !isCompaction &&
        !isReasoning &&
        index > terminalEntryIndex &&
        !isSingleTrailingActivity
      ) {
        continue;
      }
      // User input, subagent batches and warnings (a paused chat, a restarted
      // session) stay visible after their turn settles.
      if (
        entry.kind === "work" &&
        (entry.entry.questionAnswer !== undefined ||
          entry.entry.agentSpawn !== undefined ||
          entry.entry.sourceActivityKind === "runtime.warning")
      ) {
        continue;
      }
      hiddenEntryIds.add(entry.id);
    }
    if (hiddenEntryIds.size === 0) {
      continue;
    }
    // A lone compaction row stays visible on its own; it only folds away as
    // part of a turn that already folds other work. Thinking is the same: a
    // question answered by thought alone keeps its "Thought" row
    // rather than collapsing behind a "Worked for ..." that hides nothing else.
    const hidesFoldableWork = group.entries.some(
      (entry) =>
        hiddenEntryIds.has(entry.id) &&
        !(entry.kind === "work" && entry.entry.sourceActivityKind === "context-compaction") &&
        !(entry.kind === "message" && entry.message.role === "reasoning"),
    );
    if (!hidesFoldableWork) {
      continue;
    }

    const firstEntry = group.entries[0];
    const firstHiddenEntry = group.entries.find((entry) => hiddenEntryIds.has(entry.id));
    const lastEntry = group.entries.at(-1);
    if (!firstEntry || !firstHiddenEntry || !lastEntry) {
      continue;
    }

    const isLatestInterruptedTurn =
      input.latestTurn !== null &&
      group.turnIds.has(input.latestTurn.turnId) &&
      input.latestTurn.state === "interrupted";
    // A turn cut short by a steer leaves trailing work entries behind its
    // terminal message — take whichever ended last.
    const lastEntryEnd =
      lastEntry.kind === "message" ? lastEntry.message.updatedAt : lastEntry.createdAt;
    // A response spanning several turns runs from its user message to its
    // last entry; the latest turn's own start would undercount it.
    const elapsedMs =
      group.turnIds.size === 1 &&
      input.latestTurn?.turnId === turnId &&
      input.latestTurn.startedAt &&
      input.latestTurn.completedAt
        ? computeElapsedMs(input.latestTurn.startedAt, input.latestTurn.completedAt)
        : computeElapsedMs(
            group.startBoundary ?? firstEntry.createdAt,
            maxIsoTimestamp(group.terminalEntry?.message.updatedAt ?? null, lastEntryEnd) ??
              lastEntryEnd,
          );
    const duration = elapsedMs !== null ? formatDuration(elapsedMs) : null;
    // Interruption can come from server replacement as well as a user stop;
    // the persisted turn state does not identify who initiated it, so the
    // label stays neutral (upstream says "You stopped").
    const label = isLatestInterruptedTurn
      ? duration
        ? `Interrupted after ${duration}`
        : "Response interrupted"
      : duration
        ? `Worked for ${duration}`
        : "Worked";

    const directSpawnEntries = group.entries.flatMap((entry) =>
      entry.kind === "work" &&
      entry.entry.agentSpawn !== undefined &&
      entry.entry.agentSpawn.workflowId === null
        ? [{ id: entry.id, entry: entry.entry, agentSpawn: entry.entry.agentSpawn }]
        : [],
    );
    const firstDirectSpawn = directSpawnEntries[0];
    const mergedSpawn =
      firstDirectSpawn !== undefined && directSpawnEntries.length > 1
        ? {
            entryId: firstDirectSpawn.id,
            entry: {
              ...firstDirectSpawn.entry,
              agentSpawn: {
                workflowId: null,
                agentTaskIds: Array.from(
                  new Set(directSpawnEntries.flatMap((spawn) => spawn.agentSpawn.agentTaskIds)),
                ),
              },
            },
            hiddenEntryIds: new Set(directSpawnEntries.slice(1).map((spawn) => spawn.id)),
          }
        : null;

    foldsByAnchorEntryId.set(firstHiddenEntry.id, {
      turnId,
      anchorEntryId: firstHiddenEntry.id,
      createdAt: firstHiddenEntry.createdAt,
      hiddenEntryIds,
      label,
      mergedSpawn,
    });
  }
  return foldsByAnchorEntryId;
}

/**
 * The marker above a user message that reached a running reply, per how it
 * got there; null for an ordinary message. The label matches the composer's
 * follow-up actions (Steer / Interrupt); the description is its tooltip.
 * Adapted from pingdotgg/t3code (MIT), `UserMessageIntentMarker` in
 * MessagesTimeline.tsx at 7202ba666, whose intents are steer and queued turns.
 */
export function userMessageSteerMarker(
  steer: ChatMessage["steer"],
): { readonly label: string; readonly description: string } | null {
  switch (steer) {
    case "steer":
      return { label: "Steer", description: "Steered the reply in progress" };
    case "interrupt":
      return { label: "Interrupt", description: "Stopped the reply, then sent this" };
    case undefined:
      return null;
  }
}

/**
 * Plan chips an earlier turn of the same response published: between two user
 * messages only the last plan chip is shown (a delegated worker's wake turn
 * re-publishes the lead's todo list, which would otherwise show twice).
 */
export function deriveSupersededPlanEntryIds(
  timelineEntries: ReadonlyArray<TimelineEntry>,
): ReadonlySet<string> {
  const superseded = new Set<string>();
  let latestPlanEntryId: string | null = null;
  for (const entry of timelineEntries) {
    if (entry.kind === "message" && entry.message.role === "user") {
      latestPlanEntryId = null;
      continue;
    }
    if (entry.kind !== "turn-plan") continue;
    if (latestPlanEntryId !== null) superseded.add(latestPlanEntryId);
    latestPlanEntryId = entry.id;
  }
  return superseded;
}

/**
 * When a settled turn ends with tool calls after its terminal text, treat the
 * text and tools as one visual response. The message metadata becomes the
 * footer for the whole block instead of separating the prose from the tools.
 */
function attachTrailingToolGroupsToAssistant(
  rows: ReadonlyArray<MessagesTimelineRow>,
): MessagesTimelineRow[] {
  const messageRowsWithoutMeta = new Set<string>();
  const metaRowsAfterIndex = new Map<
    number,
    Extract<MessagesTimelineRow, { kind: "assistant-meta" }>
  >();

  for (const [messageIndex, row] of rows.entries()) {
    const turnId = row.kind === "message" ? (row.message.turnId ?? null) : null;
    if (
      row.kind !== "message" ||
      row.message.role !== "assistant" ||
      !row.showAssistantMeta ||
      turnId === null
    ) {
      continue;
    }

    let lastTrailingWorkIndex = -1;
    let hasTrailingToolGroup = false;
    for (let index = messageIndex + 1; index < rows.length; index += 1) {
      const candidate = rows[index];
      if (!candidate) {
        break;
      }
      // A thinking block can follow the answer (the next one starts before its
      // tool call); it is not another message in the conversation.
      if (candidate.kind === "message" && candidate.message.role === "reasoning") {
        continue;
      }
      if (candidate.kind === "message") {
        break;
      }
      if (
        (candidate.kind === "work-toggle" ||
          (candidate.kind === "activity-group" &&
            candidate.entries.some((entry) => entry.kind === "work"))) &&
        candidate.turnId === turnId
      ) {
        hasTrailingToolGroup = true;
        lastTrailingWorkIndex = index;
        continue;
      }
      if (
        candidate.kind === "work" &&
        candidate.groupedEntries.some((entry) => entry.turnId === turnId)
      ) {
        if (
          !candidate.isExpandedToolGroup &&
          candidate.groupedEntries.some(workLogEntryIsToolLike)
        ) {
          hasTrailingToolGroup = true;
        }
        if (hasTrailingToolGroup) {
          lastTrailingWorkIndex = index;
        }
      }
    }

    if (lastTrailingWorkIndex < 0) {
      continue;
    }

    messageRowsWithoutMeta.add(row.id);
    metaRowsAfterIndex.set(lastTrailingWorkIndex, {
      kind: "assistant-meta",
      id: `assistant-meta:${row.message.id}`,
      createdAt: rows[lastTrailingWorkIndex]?.createdAt ?? row.message.updatedAt,
      message: row.message,
      showAssistantCopyButton: row.showAssistantCopyButton,
      assistantCopyStreaming: row.assistantCopyStreaming,
    });
  }

  const result: MessagesTimelineRow[] = [];
  for (const [index, row] of rows.entries()) {
    if (row.kind === "message" && messageRowsWithoutMeta.has(row.id)) {
      result.push({ ...row, showAssistantMeta: false, showAssistantCopyButton: false });
    } else {
      result.push(row);
    }
    const metaRow = metaRowsAfterIndex.get(index);
    if (metaRow) {
      result.push(metaRow);
    }
  }
  return result;
}

/** Match each user message to the next assistant checkpoint. */
function buildRevertTurnCountByUserMessageId(input: {
  supportsConversationRollback: boolean;
  timelineEntries: ReadonlyArray<TimelineEntry>;
  turnDiffSummaryByAssistantMessageId: ReadonlyMap<MessageId, TurnDiffSummary>;
  inferredCheckpointTurnCountByTurnId: Readonly<Record<string, number | undefined>>;
}): Map<MessageId, number> {
  const byUserMessageId = new Map<MessageId, number>();
  const entryCount = input.supportsConversationRollback ? input.timelineEntries.length : 0;
  for (let index = 0; index < entryCount; index += 1) {
    const entry = input.timelineEntries[index];
    if (!entry || entry.kind !== "message" || entry.message.role !== "user") {
      continue;
    }

    for (let nextIndex = index + 1; nextIndex < input.timelineEntries.length; nextIndex += 1) {
      const nextEntry = input.timelineEntries[nextIndex];
      if (!nextEntry || nextEntry.kind !== "message") {
        continue;
      }
      if (nextEntry.message.role === "user") {
        break;
      }
      const summary = input.turnDiffSummaryByAssistantMessageId.get(nextEntry.message.id);
      if (!summary) {
        continue;
      }
      const turnCount =
        summary.checkpointTurnCount ?? input.inferredCheckpointTurnCountByTurnId[summary.turnId];
      if (typeof turnCount !== "number") {
        break;
      }
      byUserMessageId.set(entry.message.id, Math.max(0, turnCount - 1));
      break;
    }
  }
  return byUserMessageId;
}

export function deriveMessagesTimelineRows(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  latestTurn?: TimelineLatestTurn | null;
  runningTurnId?: TurnId | null;
  expandedTurnIds?: ReadonlySet<TurnId>;
  expandedWorkGroupIds?: ReadonlySet<string>;
  isWorking: boolean;
  activeTurnStartedAt: string | null;
  turnDiffSummaries: ReadonlyArray<TurnDiffSummary>;
  supportsConversationRollback: boolean;
  /** Task ids of subagents still working, used by the active tool indicator. */
  liveAgentTaskIds?: ReadonlySet<string> | undefined;
}): MessagesTimelineRow[] {
  const turnDiffSummaryByAssistantMessageId = new Map<MessageId, TurnDiffSummary>();
  for (const summary of input.turnDiffSummaries) {
    if (summary.assistantMessageId) {
      turnDiffSummaryByAssistantMessageId.set(summary.assistantMessageId, summary);
    }
  }
  const revertTurnCountByUserMessageId = buildRevertTurnCountByUserMessageId({
    supportsConversationRollback: input.supportsConversationRollback,
    timelineEntries: input.timelineEntries,
    turnDiffSummaryByAssistantMessageId,
    inferredCheckpointTurnCountByTurnId: input.supportsConversationRollback
      ? inferCheckpointTurnCountByTurnId(input.turnDiffSummaries)
      : {},
  });
  const nextRows: MessagesTimelineRow[] = [];
  const durationStartByMessageId = computeMessageDurationStart(
    input.timelineEntries.flatMap((entry) => (entry.kind === "message" ? [entry.message] : [])),
  );
  const terminalAssistantMessageIds = deriveTerminalAssistantMessageIds(input.timelineEntries);
  const unsettledTurnId = deriveUnsettledTurnId(
    input.latestTurn ?? null,
    input.runningTurnId ?? null,
  );
  const activeVisualResponseTurnIds = deriveActiveVisualResponseTurnIds({
    timelineEntries: input.timelineEntries,
    unsettledTurnId,
    isWorking: input.isWorking,
  });
  const supersededPlanEntryIds = deriveSupersededPlanEntryIds(input.timelineEntries);
  const liveAgentTaskIds = input.liveAgentTaskIds ?? new Set<string>();
  const foldsByAnchorEntryId = deriveTurnFolds({
    timelineEntries: input.timelineEntries,
    terminalAssistantMessageIds,
    latestTurn: input.latestTurn ?? null,
    unfoldedTurnIds: activeVisualResponseTurnIds,
    liveAgentTaskIds,
  });
  const collapsedEntryIds = new Set<string>();
  const mergedSpawnEntryById = new Map<string, WorkLogEntry>();
  for (const fold of foldsByAnchorEntryId.values()) {
    if (!input.expandedTurnIds?.has(fold.turnId)) {
      for (const entryId of fold.hiddenEntryIds) {
        collapsedEntryIds.add(entryId);
      }
      if (fold.mergedSpawn) {
        mergedSpawnEntryById.set(fold.mergedSpawn.entryId, fold.mergedSpawn.entry);
        for (const entryId of fold.mergedSpawn.hiddenEntryIds) {
          collapsedEntryIds.add(entryId);
        }
      }
    }
  }

  let activeTurnHeaderIndex = input.timelineEntries.length;
  if (input.isWorking) {
    const latestUserMessageIndex = lastUserMessageIndex(input.timelineEntries);
    activeTurnHeaderIndex = latestUserMessageIndex + 1;
  }
  const entryBelongsToActiveTurn = (entry: TimelineEntry, index: number) =>
    input.isWorking &&
    index >= activeTurnHeaderIndex &&
    (unsettledTurnId === null || timelineEntryTurnId(entry) === unsettledTurnId);
  const workEntryIsInActiveRun = (entry: WorkLogEntry) =>
    input.isWorking &&
    unsettledTurnId !== null &&
    entry.toolLifecycleStatus === "inProgress" &&
    entry.turnId === unsettledTurnId;
  const activeToolEntries: Array<Extract<TimelineEntry, { kind: "work" }>> = [];
  // Without an unsettled turn, isWorking can still be true (connecting, checkpoint
  // revert, or a send that has not been assigned a turn yet). Do not promote
  // settled historical tools after the last user message into a live row.
  if (unsettledTurnId !== null) {
    for (let index = input.timelineEntries.length - 1; index >= activeTurnHeaderIndex; index -= 1) {
      const entry = input.timelineEntries[index]!;
      // Agent-spawn CTA rows never join the live group: a running fleet must
      // stay visible as its own row (with the roster) instead of hiding
      // behind whichever tool the parent ran next.
      if (
        !entryBelongsToActiveTurn(entry, index) ||
        entry.kind !== "work" ||
        entry.entry.agentSpawn !== undefined ||
        entry.entry.questionAnswer !== undefined ||
        entry.entry.sourceActivityKind === "context-compaction" ||
        entry.entry.sourceActivityKind === "runtime.warning" ||
        entry.entry.tone === "error"
      ) {
        break;
      }
      activeToolEntries.unshift(entry);
      if (workEntryDisplayIndicatesToolFailure(entry.entry)) break;
    }
  }
  const visibleActiveToolEntries = omitSupersededLifecycleMarkers(
    activeToolEntries.filter((entry) => workEntryIsVisibleInGroup(entry.entry, true)),
    (entry) => entry.entry,
  );
  const activeWorkAnchor = activeToolEntries[0];
  const latestVisibleToolEntry = visibleActiveToolEntries.at(-1);
  const latestRunningToolEntry = visibleActiveToolEntries.findLast((entry) => {
    return entry.entry.agentSpawn
      ? entry === latestVisibleToolEntry && spawnEntryIsLive(entry.entry, liveAgentTaskIds)
      : workEntryIsActiveTurnActivity(entry.entry);
  });
  const latestToolFailed =
    latestRunningToolEntry === undefined &&
    latestVisibleToolEntry !== undefined &&
    latestVisibleToolEntry.entry.toolLifecycleStatus !== "declined" &&
    workEntryDisplayIndicatesToolFailure(latestVisibleToolEntry.entry);
  const latestToolKeepsActivityLive =
    latestRunningToolEntry !== undefined ||
    (latestVisibleToolEntry !== undefined &&
      latestVisibleToolEntry.entry.agentSpawn === undefined &&
      (workEntryIndicatesToolSuccess(latestVisibleToolEntry.entry) ||
        (latestVisibleToolEntry.entry.toolLifecycleStatus === "completed" &&
          !workEntryDisplayIndicatesToolFailure(latestVisibleToolEntry.entry))));
  const activeWorkPlacementEntryId = latestVisibleToolEntry?.id;
  const activeWorkRow =
    activeWorkAnchor && latestVisibleToolEntry && !latestToolFailed
      ? (() => {
          const groupId = workGroupId(activeWorkAnchor.id, activeWorkAnchor.entry);
          return {
            kind: "work-live" as const,
            id: latestToolKeepsActivityLive
              ? LIVE_ACTIVITY_ROW_ID
              : `work-live:${workGroupIdentity(activeWorkAnchor.id, activeWorkAnchor.entry)}`,
            createdAt: activeWorkAnchor.createdAt,
            entry: (latestRunningToolEntry ?? latestVisibleToolEntry).entry,
            groupedEntries: visibleActiveToolEntries.map((entry) => entry.entry),
            groupId,
            expanded: input.expandedWorkGroupIds?.has(groupId) ?? false,
            active: latestToolKeepsActivityLive,
          };
        })()
      : null;
  const activeWorkEntryIds = new Set(
    activeWorkRow !== null || latestToolFailed ? activeToolEntries.map((entry) => entry.id) : [],
  );
  const appendWorkingRow = () => {
    const latestUserMessage = input.timelineEntries[lastUserMessageIndex(input.timelineEntries)];
    const visualResponseStartedAt =
      activeVisualResponseTurnIds.size > 1 &&
      latestUserMessage?.kind === "message" &&
      latestUserMessage.message.role === "user"
        ? latestUserMessage.message.createdAt
        : input.activeTurnStartedAt;
    nextRows.push({
      kind: "working",
      id: "working-indicator-row",
      createdAt: visualResponseStartedAt,
    });
  };
  let hasActivityRow = false;
  const appendActiveWorkRows = () => {
    if (activeWorkRow === null) return;
    nextRows.push(activeWorkRow);
    hasActivityRow ||= activeWorkRow.active;
    if (!activeWorkRow.expanded || activeWorkRow.entry.agentSpawn) return;
    nextRows.push(
      expandedWorkGroupRow(
        activeWorkRow.groupId,
        activeWorkRow.createdAt,
        activeWorkRow.groupedEntries,
      ),
    );
  };

  let scannedActivityThrough = -1;
  for (let index = 0; index < input.timelineEntries.length; index += 1) {
    const timelineEntry = input.timelineEntries[index];
    if (!timelineEntry) {
      continue;
    }

    if (input.isWorking && index === activeTurnHeaderIndex) {
      appendWorkingRow();
    }

    if (timelineEntry.id === activeWorkPlacementEntryId) {
      appendActiveWorkRows();
    }

    const anchoredTurnFold = foldsByAnchorEntryId.get(timelineEntry.id);
    if (anchoredTurnFold) {
      nextRows.push({
        kind: "turn-fold",
        id: `turn-fold:${anchoredTurnFold.turnId}`,
        createdAt: anchoredTurnFold.createdAt,
        turnId: anchoredTurnFold.turnId,
        label: anchoredTurnFold.label,
        expanded: input.expandedTurnIds?.has(anchoredTurnFold.turnId) ?? false,
      });
    }

    if (collapsedEntryIds.has(timelineEntry.id)) {
      continue;
    }

    const activityTurnId = timelineEntryTurnId(timelineEntry);
    if (index > scannedActivityThrough && activityTurnId && isActivityEntry(timelineEntry)) {
      const entries = [timelineEntry];
      let cursor = index + 1;
      while (cursor < input.timelineEntries.length) {
        const next = input.timelineEntries[cursor]!;
        if (
          !isActivityEntry(next) ||
          timelineEntryTurnId(next) !== activityTurnId ||
          collapsedEntryIds.has(next.id) ||
          foldsByAnchorEntryId.has(next.id)
        )
          break;
        entries.push(next);
        cursor += 1;
      }
      scannedActivityThrough = cursor - 1;
      if (entries.some((entry) => entry.kind === "message")) {
        const active =
          input.isWorking &&
          activityTurnId === unsettledTurnId &&
          cursor === input.timelineEntries.length &&
          !latestToolFailed &&
          (latestVisibleToolEntry === undefined || latestToolKeepsActivityLive);
        const groupId =
          timelineEntry.kind === "work"
            ? workGroupId(timelineEntry.id, timelineEntry.entry)
            : `activity-group:${timelineEntry.id}`;
        nextRows.push({
          kind: "activity-group",
          id: active ? LIVE_ACTIVITY_ROW_ID : groupId,
          createdAt: timelineEntry.createdAt,
          turnId: activityTurnId,
          groupId,
          entries,
          expanded: input.expandedWorkGroupIds?.has(groupId) ?? false,
          active,
        });
        hasActivityRow ||= active;
        index = cursor - 1;
        continue;
      }
    }

    if (activeWorkEntryIds.has(timelineEntry.id)) {
      continue;
    }

    if (
      timelineEntry.kind === "work" &&
      timelineEntry.entry.sourceActivityKind === "context-compaction"
    ) {
      nextRows.push({
        kind: "context-compaction",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        label: timelineEntry.entry.label,
      });
      continue;
    }

    if (timelineEntry.kind === "work") {
      if (
        timelineEntry.entry.agentSpawn !== undefined ||
        timelineEntry.entry.questionAnswer !== undefined ||
        timelineEntry.entry.sourceActivityKind === "runtime.warning" ||
        timelineEntry.entry.tone === "error"
      ) {
        if (entryBelongsToActiveTurn(timelineEntry, index)) {
          hasActivityRow ||= spawnEntryIsLive(timelineEntry.entry, liveAgentTaskIds);
        }
        nextRows.push({
          kind: "work",
          id: timelineEntry.id,
          createdAt: timelineEntry.createdAt,
          groupedEntries: [mergedSpawnEntryById.get(timelineEntry.id) ?? timelineEntry.entry],
          isExpandedToolGroup: false,
        });
        continue;
      }
      const groupedEntries = [timelineEntry.entry];
      let cursor = index + 1;
      while (cursor < input.timelineEntries.length) {
        const nextEntry = input.timelineEntries[cursor];
        if (
          !nextEntry ||
          nextEntry.kind !== "work" ||
          nextEntry.entry.agentSpawn !== undefined ||
          nextEntry.entry.questionAnswer !== undefined ||
          nextEntry.entry.sourceActivityKind === "context-compaction" ||
          nextEntry.entry.sourceActivityKind === "runtime.warning" ||
          nextEntry.entry.tone === "error" ||
          activeWorkEntryIds.has(nextEntry.id) ||
          collapsedEntryIds.has(nextEntry.id) ||
          foldsByAnchorEntryId.has(nextEntry.id)
        ) {
          break;
        }
        groupedEntries.push(nextEntry.entry);
        cursor += 1;
      }
      const visibleGroupedEntries = omitSupersededLifecycleMarkers(
        groupedEntries.filter((entry) =>
          workEntryIsVisibleInGroup(entry, workEntryIsInActiveRun(entry)),
        ),
        (entry) => entry,
      );
      if (visibleGroupedEntries.length > 0) {
        const activeInProgressToolEntries = visibleGroupedEntries.filter(workEntryIsInActiveRun);
        if (activeInProgressToolEntries.length > 0) {
          const groupId = workGroupId(timelineEntry.id, timelineEntry.entry);
          const expanded = input.expandedWorkGroupIds?.has(groupId) ?? false;
          const latestActiveToolEntry = activeInProgressToolEntries.at(-1)!;
          nextRows.push({
            kind: "work-live",
            id: `work-live:${workGroupIdentity(timelineEntry.id, timelineEntry.entry)}`,
            createdAt: timelineEntry.createdAt,
            entry: latestActiveToolEntry,
            groupedEntries: visibleGroupedEntries,
            groupId,
            expanded,
            active: true,
          });
          hasActivityRow = true;
          if (expanded) {
            nextRows.push(
              expandedWorkGroupRow(groupId, timelineEntry.createdAt, visibleGroupedEntries),
            );
          }
        } else if (
          visibleGroupedEntries.length === 1 &&
          workLogEntryIsToolLike(visibleGroupedEntries[0]!)
        ) {
          // A lone tool call reads as itself, not as a "Used 1 tool" summary.
          const singleEntry = visibleGroupedEntries[0]!;
          nextRows.push({
            kind: "work",
            id: timelineEntry.id,
            createdAt: timelineEntry.createdAt,
            groupedEntries: visibleGroupedEntries,
            isExpandedToolGroup: false,
            ...(toolGroupAction(singleEntry) === "edit"
              ? { displayLabel: summarizeToolGroup(visibleGroupedEntries) }
              : {}),
          });
        } else {
          const groupId = workGroupId(timelineEntry.id, timelineEntry.entry);
          const expanded = input.expandedWorkGroupIds?.has(groupId) ?? false;
          const summaryKind = toolGroupSummaryKind(visibleGroupedEntries);
          const latestToolEntry = visibleGroupedEntries.findLast(workLogEntryIsToolLike);
          const singleEntry =
            visibleGroupedEntries.length === 1 ? (visibleGroupedEntries[0] ?? null) : null;
          nextRows.push({
            kind: "work-toggle",
            id: `work-toggle:${timelineEntry.id}`,
            createdAt: timelineEntry.createdAt,
            turnId: timelineEntry.entry.turnId ?? null,
            groupId,
            hiddenCount: visibleGroupedEntries.length,
            expanded,
            // A single note (a plan update, a status line) is its own summary.
            summary:
              singleEntry !== null && !workLogEntryIsToolLike(singleEntry)
                ? singleEntry.label
                : summarizeToolGroup(visibleGroupedEntries),
            summaryKind,
            ...(summaryKind === "browser" && visibleGroupedEntries.at(-1)?.toolWebsiteUrl
              ? { toolWebsiteUrl: visibleGroupedEntries.at(-1)!.toolWebsiteUrl! }
              : {}),
            ...(() => {
              const connectorId = soleConnectorId(visibleGroupedEntries);
              return connectorId !== null ? { connectorId } : {};
            })(),
            hasFailure:
              latestToolEntry !== undefined &&
              workEntryDisplayIndicatesToolFailure(latestToolEntry),
          });
          if (expanded) {
            nextRows.push(
              expandedWorkGroupRow(groupId, timelineEntry.createdAt, visibleGroupedEntries),
            );
          }
        }
      }
      index = cursor - 1;
      continue;
    }

    if (timelineEntry.kind === "turn-plan") {
      // One plan per response: a woken turn re-publishes the same todo list,
      // and only the latest state of it is worth a chip.
      if (supersededPlanEntryIds.has(timelineEntry.id)) {
        continue;
      }
      nextRows.push({
        kind: "turn-plan",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        turnPlan: timelineEntry.turnPlan,
      });
      continue;
    }

    const assistantResponseStillInProgress =
      timelineEntry.message.role === "assistant" &&
      timelineEntry.message.turnId !== null &&
      timelineEntry.message.turnId !== undefined &&
      activeVisualResponseTurnIds.has(timelineEntry.message.turnId);

    const durationStart =
      durationStartByMessageId.get(timelineEntry.message.id) ?? timelineEntry.message.createdAt;

    // While the turn is still running, the latest assistant message is only
    // provisionally terminal — withhold the metadata row until the turn
    // settles so commentary doesn't flash timestamps mid-work.
    const showAssistantMeta =
      timelineEntry.message.role === "assistant" &&
      terminalAssistantMessageIds.has(timelineEntry.message.id) &&
      !assistantResponseStillInProgress;

    nextRows.push({
      kind: "message",
      id: timelineEntry.id,
      createdAt: timelineEntry.createdAt,
      message: timelineEntry.message,
      durationStart,
      showAssistantMeta,
      showAssistantCopyButton: showAssistantMeta,
      assistantCopyStreaming: timelineEntry.message.streaming || assistantResponseStillInProgress,
      assistantTurnDiffSummary:
        timelineEntry.message.role === "assistant"
          ? turnDiffSummaryByAssistantMessageId.get(timelineEntry.message.id)
          : undefined,
      revertTurnCount:
        timelineEntry.message.role === "user"
          ? revertTurnCountByUserMessageId.get(timelineEntry.message.id)
          : undefined,
    });
  }

  if (input.isWorking && activeTurnHeaderIndex === input.timelineEntries.length) {
    appendWorkingRow();
  }
  // The turn is live but nothing in the log is: show the thinking placeholder
  // as the single live activity row (also after the latest tool failed).
  if (input.isWorking && (!hasActivityRow || latestToolFailed)) {
    nextRows.push({
      kind: "thinking",
      id: LIVE_ACTIVITY_ROW_ID,
      createdAt: input.activeTurnStartedAt,
    });
  }
  return attachTrailingToolGroupsToAssistant(nextRows);
}

type MessagesTimelineRowsInput = Parameters<typeof deriveMessagesTimelineRows>[0];

export interface MessagesTimelineRowsProjection {
  readonly input: MessagesTimelineRowsInput;
  readonly rows: MessagesTimelineRow[];
}

function replaceStreamingMessageRows(
  input: MessagesTimelineRowsInput,
  previous: MessagesTimelineRowsProjection,
): MessagesTimelineRow[] | null {
  const {
    timelineEntries: previousEntries,
    turnDiffSummaries: previousSummaries,
    latestTurn: previousLatestTurn,
    expandedTurnIds: previousExpandedTurns,
    expandedWorkGroupIds: previousExpandedGroups,
    liveAgentTaskIds: previousLiveAgentTaskIds,
    ...previousContext
  } = previous.input;
  const {
    timelineEntries,
    turnDiffSummaries,
    latestTurn,
    expandedTurnIds,
    expandedWorkGroupIds,
    liveAgentTaskIds,
    ...context
  } = input;
  if (
    timelineEntries.length !== previousEntries.length ||
    !shallow(previousContext, context) ||
    !shallow(previousSummaries, turnDiffSummaries) ||
    !shallow(previousLatestTurn, latestTurn) ||
    !shallow(previousExpandedTurns, expandedTurnIds) ||
    !shallow(previousExpandedGroups, expandedWorkGroupIds) ||
    !shallow(previousLiveAgentTaskIds, liveAgentTaskIds)
  ) {
    return null;
  }
  const replacements = new Map<ChatMessage, ChatMessage>();
  for (const [index, entry] of timelineEntries.entries()) {
    const previousEntry = previousEntries[index]!;
    if (entry === previousEntry || shallow(entry, previousEntry)) continue;
    if (
      entry.kind !== "message" ||
      previousEntry.kind !== "message" ||
      entry.id !== previousEntry.id ||
      entry.createdAt !== previousEntry.createdAt
    ) {
      return null;
    }
    if (entry.message === previousEntry.message) continue;
    if (!isStreamingMessageTextUpdate(previousEntry.message, entry.message)) return null;
    replacements.set(previousEntry.message, entry.message);
  }
  if (replacements.size === 0) return previous.rows;
  return previous.rows.map((row) => {
    if (row.kind === "activity-group") {
      if (!row.entries.some((entry) => entry.kind === "message" && replacements.has(entry.message)))
        return row;
      return {
        ...row,
        entries: row.entries.map((entry) => {
          if (entry.kind !== "message") return entry;
          const message = replacements.get(entry.message);
          return message ? { ...entry, message } : entry;
        }),
      };
    }
    if (row.kind !== "message" && row.kind !== "assistant-meta") return row;
    const message = replacements.get(row.message);
    return message ? { ...row, message } : row;
  });
}

/** Keep one projection per timeline. Reuse rows only when streaming content changes. */
export function deriveMessagesTimelineRowsWithState(
  input: MessagesTimelineRowsInput,
  previous: MessagesTimelineRowsProjection | null = null,
): MessagesTimelineRowsProjection {
  return {
    input,
    rows:
      (previous === null ? null : replaceStreamingMessageRows(input, previous)) ??
      deriveMessagesTimelineRows(input),
  };
}

export function computeStableMessagesTimelineRows(
  rows: MessagesTimelineRow[],
  previous: StableMessagesTimelineRowsState,
): StableMessagesTimelineRowsState {
  const next = new Map<string, MessagesTimelineRow>();
  let anyChanged = rows.length !== previous.byId.size;

  const result = rows.map((row, index) => {
    const prevRow = previous.byId.get(row.id);
    const nextRow = prevRow && isRowUnchanged(prevRow, row) ? prevRow : row;
    next.set(row.id, nextRow);
    if (!anyChanged && previous.result[index] !== nextRow) {
      anyChanged = true;
    }
    return nextRow;
  });

  return anyChanged ? { byId: next, result } : previous;
}

/** Shallow field comparison per row variant — avoids deep equality cost. */
function isRowUnchanged(a: MessagesTimelineRow, b: MessagesTimelineRow): boolean {
  if (a.kind !== b.kind || a.id !== b.id) return false;

  switch (a.kind) {
    case "activity-group": {
      const group = b as typeof a;
      return (
        a.active === group.active &&
        a.expanded === group.expanded &&
        a.groupId === group.groupId &&
        a.entries.length === group.entries.length &&
        a.entries.every((entry, index) => entry === group.entries[index])
      );
    }

    case "working":
    case "thinking":
      return a.createdAt === (b as typeof a).createdAt;

    case "assistant-meta": {
      const bm = b as typeof a;
      return (
        a.createdAt === bm.createdAt &&
        a.message === bm.message &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantCopyStreaming === bm.assistantCopyStreaming
      );
    }

    case "turn-fold": {
      const bf = b as typeof a;
      return a.createdAt === bf.createdAt && a.label === bf.label && a.expanded === bf.expanded;
    }

    case "context-compaction": {
      const bc = b as typeof a;
      return a.createdAt === bc.createdAt && a.label === bc.label;
    }

    case "turn-plan": {
      const bp = b as typeof a;
      // Plans rewrite in place: compare the snapshot's identity fields so an
      // unchanged plan keeps its row reference (virtualization stability).
      return a.createdAt === bp.createdAt && a.turnPlan.plan === bp.turnPlan.plan;
    }

    case "work": {
      const bw = b as typeof a;
      return (
        a.isExpandedToolGroup === bw.isExpandedToolGroup &&
        a.displayLabel === bw.displayLabel &&
        Equal.equals(a.groupedEntries, bw.groupedEntries)
      );
    }

    case "work-live": {
      const bw = b as typeof a;
      return (
        a.createdAt === bw.createdAt &&
        a.groupId === bw.groupId &&
        a.expanded === bw.expanded &&
        a.active === bw.active &&
        Equal.equals(a.entry, bw.entry) &&
        Equal.equals(a.groupedEntries, bw.groupedEntries)
      );
    }

    case "work-toggle": {
      const bw = b as typeof a;
      return (
        a.createdAt === bw.createdAt &&
        a.turnId === bw.turnId &&
        a.groupId === bw.groupId &&
        a.hiddenCount === bw.hiddenCount &&
        a.expanded === bw.expanded &&
        a.summary === bw.summary &&
        a.summaryKind === bw.summaryKind &&
        a.toolWebsiteUrl === bw.toolWebsiteUrl &&
        a.connectorId === bw.connectorId &&
        a.hasFailure === bw.hasFailure
      );
    }

    case "message": {
      const bm = b as typeof a;
      return (
        a.message === bm.message &&
        a.durationStart === bm.durationStart &&
        a.showAssistantMeta === bm.showAssistantMeta &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantCopyStreaming === bm.assistantCopyStreaming &&
        a.assistantTurnDiffSummary === bm.assistantTurnDiffSummary &&
        a.revertTurnCount === bm.revertTurnCount
      );
    }
  }
}

/** Save the row at the current offset; a virtualizer's cached visible range can lag a fling. */
export function resolveWorkGroupScrollAnchor(state: {
  readonly data: ReadonlyArray<{ readonly id: string }>;
  readonly scroll: number;
  readonly positionAtIndex: (index: number) => number | undefined;
}) {
  if (state.data.length === 0 || !Number.isFinite(state.scroll)) return undefined;
  const scrollOffset = Math.max(0, state.scroll);
  let low = 0;
  let high = state.data.length - 1;
  let index = 0;
  let rowTop = state.positionAtIndex(0);
  if (rowTop === undefined || !Number.isFinite(rowTop)) return undefined;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const top = state.positionAtIndex(middle);
    if (top === undefined || !Number.isFinite(top)) return undefined;
    if (top <= scrollOffset) {
      index = middle;
      rowTop = top;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return {
    rowId: state.data[index]!.id,
    offsetWithinRow: Math.max(0, scrollOffset - rowTop),
    scrollOffset,
  };
}
